import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType } from '@azure/msal-browser';
import { BehaviorSubject, Subject, filter, takeUntil, firstValueFrom, timeout, catchError, of } from 'rxjs';
import { groupIds, apiScopes } from '../auth-config';
import { environment } from '../../environments/environment';

export interface UserInfo {
  displayName: string;
  email: string;
  groups: string[];
  isAdmin: boolean;
  isPM: boolean;
  isFinance: boolean;
  isPc: boolean;
  isSoftware: boolean;
  isAccounting: boolean;
  isMarketing: boolean;
  isSales: boolean;
  isIt: boolean;
  isLnD: boolean;
  isOperations: boolean;
  isSupport: boolean;
}

/** Backend /User/me computed access roles (single source of truth when API is used). */
export interface BackendAccessRoles {
  isAdmin: boolean;
  isPM: boolean;
  isFinance: boolean;
  isPc: boolean;
  isSoftware: boolean;
  isAccounting: boolean;
  isMarketing: boolean;
  isSales: boolean;
  isIt: boolean;
  isLnD: boolean;
  isOperations: boolean;
  isSupport: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private userSubject = new BehaviorSubject<UserInfo | null>(null);
  public user$ = this.userSubject.asObservable();

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private isLoadingUserInfoSubject = new BehaviorSubject<boolean>(false);
  public isLoadingUserInfo$ = this.isLoadingUserInfoSubject.asObservable();

  private isLoginInProgress = false;
  private loginRetryCount = 0;
  private readonly MAX_LOGIN_RETRIES = 3;
  private initializationPromise: Promise<void>;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private http: HttpClient
  ) {
    this.initializationPromise = this.initializeMsal();
    this.setupMsalErrorHandling();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup MSAL error event listeners to handle token refresh failures
   */
  private setupMsalErrorHandling(): void {
    // Listen for MSAL token acquisition failures
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        takeUntil(this.destroy$)
      )
      .subscribe((message: EventMessage) => {
        // The payload for ACQUIRE_TOKEN_FAILURE contains the error
        const payload = message.payload as any;
        const error = payload?.error || payload;
        
        // If it's a 400 error (invalid_grant), the refresh token is expired
        // MSAL interceptor will handle redirect, but we should clear cache
        if (error?.errorCode === 'invalid_grant' || 
            error?.status === 400 || 
            error?.errorMessage?.includes('AADSTS70008') || // Expired refresh token
            error?.errorMessage?.includes('AADSTS700082') ||
            error?.errorMessage?.includes('AADSTS50173') || // Fresh authentication required
            error?.message?.includes('invalid_grant') ||
            error?.message?.includes('Bad Request')) {
          // Clear cache - MSAL interceptor will trigger redirect
          this.clearMsalCache();
        }
      });
  }

  private async initializeMsal(): Promise<void> {
    try {
      await this.msalService.instance.initialize();
      
      // CRITICAL: Handle redirect promise FIRST before any other operations
      // This prevents "interaction_in_progress" errors
      try {
        await this.msalService.instance.handleRedirectPromise();
      } catch (error: any) {
        // If interaction_in_progress error, wait and retry
        if (error?.errorCode === 'interaction_in_progress') {
          await new Promise(resolve => setTimeout(resolve, 1000));
          try {
            await this.msalService.instance.handleRedirectPromise();
          } catch (retryError) {
            // Clear interaction state if still failing
            this.clearInteractionState();
          }
        }
      }
      
      // Check if we're coming from a redirect BEFORE processing accounts
      const LOADING_KEY = 'apex.login.loading';
      const isRedirect = window.location.hash || 
                        window.location.search.includes('code=') || 
                        window.location.search.includes('state=') ||
                        window.location.search.includes('error=') ||
                        sessionStorage.getItem(LOADING_KEY) === 'true';
      
      if (isRedirect) {
        // Show loading status immediately when returning from Azure redirect
        this.isLoadingUserInfoSubject.next(true);
        sessionStorage.setItem(LOADING_KEY, 'true');
      }
      
      // Wait a bit for MSAL to process redirect response
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        // Set the first account as active (or use the account from redirect)
        const account = accounts[0];
        this.msalService.instance.setActiveAccount(account);
        // Wait a bit more for account to be fully set
        await new Promise(resolve => setTimeout(resolve, 50));
        this.checkAuthStatus();
      } else {
        // MSAL will handle redirect response automatically, but we need to wait for it
        this.checkAuthStatus();
      }
    } catch (error) {
      // Silently handle initialization errors
    }
  }

  async checkAuthStatus(): Promise<void> {
    const account = this.msalService.instance.getActiveAccount();
    const LOADING_KEY = 'apex.login.loading';
    if (account) {
      this.isAuthenticatedSubject.next(true);
      // Set loading state BEFORE starting user info load
      this.isLoadingUserInfoSubject.next(true);
      sessionStorage.setItem(LOADING_KEY, 'true');
      try {
        await this.loadUserInfo();
      } catch (error) {
        // On error, still clear loading state
        console.error('Error loading user info:', error);
      } finally {
        // Only clear loading state after user info is fully loaded
        // Small delay to ensure UI updates smoothly
        setTimeout(() => {
          this.isLoadingUserInfoSubject.next(false);
          sessionStorage.removeItem(LOADING_KEY);
        }, 100);
      }

      // Don't proactively acquire token - let the MSAL interceptor handle it when API calls are made
      // This prevents token acquisition conflicts and delays
      // The interceptor will automatically acquire tokens when needed
    } else {
      // No account - clear loading state
      this.isLoadingUserInfoSubject.next(false);
      sessionStorage.removeItem(LOADING_KEY);
    }
  }

  async login(): Promise<void> {
    // Prevent multiple login attempts
    if (this.isLoginInProgress) {
      return;
    }

    // Check if we've exceeded max retries
    if (this.loginRetryCount >= this.MAX_LOGIN_RETRIES) {
      this.loginRetryCount = 0;
      throw new Error('Login failed after maximum retries. Please refresh the page and try again.');
    }

    // Always clear interaction state before login to ensure clean start
    this.clearInteractionState();

    // Wait for MSAL to be initialized
    try {
      await this.initializationPromise;
    } catch (error) {
      return;
    }

    // Check if an interaction is already in progress - if so, clear it
    try {
      const inProgress = await this.msalService.instance.handleRedirectPromise();
      if (inProgress) {
        // There's a redirect in progress - clear it and continue
        this.clearInteractionState();
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    } catch (error: any) {
      // Clear any stale interaction state
      this.clearInteractionState();
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    this.isLoginInProgress = true;

    try {
      // Use loginRedirect to match msalGuardConfig and msalInterceptorConfig (InteractionType.Redirect)
      // Force account selection and login prompt to ensure user sees MSAL screen
      this.msalService.loginRedirect({
        scopes: apiScopes.scopes,
        prompt: 'select_account', // Force account selection to avoid cached issues
        extraQueryParameters: {
          prompt: 'select_account' // Additional prompt parameter
        }
      });
      // Note: loginRedirect doesn't return a result - MSAL handles the redirect flow
      // After redirect, checkAuthStatus() will be called automatically
    } catch (error: any) {
      this.isLoginInProgress = false;
      // Handle interaction_in_progress error - clear and retry
      if (error?.errorCode === 'interaction_in_progress') {
        // Clear interaction state and retry after delay
        this.clearInteractionState();
        await new Promise(resolve => setTimeout(resolve, 1000));
        // Increment retry counter BEFORE recursing
        this.loginRetryCount++;
        // Return the promise from the recursive call
        return this.login();
      } else {
        // Reset retry counter on other errors
        this.loginRetryCount = 0;
        // Clear interaction state on other errors
        this.clearInteractionState();
      }
    }
  }

  private async acquireAccessToken(): Promise<void> {
    try {
      const account = this.msalService.instance.getActiveAccount();
      if (!account) {
        return;
      }

      // Try to acquire token silently - don't fail if it doesn't work
      // The MSAL interceptor will handle token acquisition for API calls
      await this.msalService.instance.acquireTokenSilent({
        scopes: apiScopes.scopes,
        account: account
      }).catch(() => {
        // Silently fail - the interceptor will handle token acquisition when needed
        // Don't use redirect here as it causes page reloads and delays
      });
    } catch (error) {
      // Silently handle error - let the MSAL interceptor handle token acquisition
      // when API calls are made
    }
  }

  /**
   * Ensures a valid access token is available before making API calls
   * Returns true if token is available or can be acquired, false otherwise
   */
  async ensureValidToken(): Promise<boolean> {
    try {
      const account = this.msalService.instance.getActiveAccount();
      if (!account) {
        return false;
      }

      // Try to get token silently
      const result = await this.msalService.instance.acquireTokenSilent({
        scopes: apiScopes.scopes,
        account: account
      });
      
      return !!result?.accessToken;
    } catch (error: any) {
      // Check if error is due to interaction required
      if (error?.errorCode === 'interaction_required' || error?.errorCode === 'consent_required') {
        // Don't trigger redirect here - let the interceptor handle it
        // Returning false will allow the interceptor to handle the token acquisition
        return false;
      }
      
      // For other errors (like invalid refresh token), clear cache and return false
      if (error?.errorCode === 'invalid_grant' || error?.status === 400) {
        // Clear potentially corrupted cache
        this.clearInteractionState();
        return false;
      }
      
      return false;
    }
  }

  logout(): void {
    // Prevent multiple logout calls
    if (this.isLoginInProgress) {
      return;
    }
    
    // Set flag to prevent re-entry
    this.isLoginInProgress = true;

    // Use local logout to clear everything without MSAL redirect
    this.localLogout();
    
    // Reset flag after a short delay
    setTimeout(() => {
      this.isLoginInProgress = false;
    }, 500);
  }

  // Local logout without MSAL redirect - clears all state and cache
  localLogout(): void {
    // Reset login in progress flag first
    this.isLoginInProgress = false;

    // Clear interaction state aggressively
    this.clearInteractionState();

    // Clear local state
    this.userSubject.next(null);
    this.isAuthenticatedSubject.next(false);

    // Clear MSAL active account
    try {
      this.msalService.instance.setActiveAccount(null);
    } catch (e) {
      // Ignore errors
    }

    // Clear all MSAL cache
    this.clearMsalCache();

    // Force clear any remaining interaction state after a short delay
    setTimeout(() => {
      this.clearInteractionState();
    }, 100);
  }

  private clearInteractionState(): void {
    try {
      // Clear MSAL's internal interaction state
      const cacheManager = (this.msalService.instance as any).browserStorage;
      if (cacheManager) {
        try {
          cacheManager.setInteractionInProgress(false);
        } catch (e) {
          // Ignore errors
        }
      }

      // Clear the interaction in progress flag from cache
      const cacheKeys = this.msalService.instance.getAllAccounts().map(account => account.homeAccountId);
      cacheKeys.forEach(key => {
        try {
          sessionStorage.removeItem(`${key}-interaction-in-progress`);
          localStorage.removeItem(`${key}-interaction-in-progress`);
        } catch (e) {
          // Ignore errors
        }
      });

      // Also try to clear generic interaction state
      try {
        sessionStorage.removeItem('interaction-in-progress');
        localStorage.removeItem('interaction-in-progress');
        // Clear MSAL interaction keys
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.includes('interaction') || key.includes('login.microsoftonline.com'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => {
          try {
            localStorage.removeItem(key);
          } catch (e) {
            // Ignore errors
          }
        });
      } catch (e) {
        // Ignore errors
      }
    } catch (error) {
      // Silently handle errors
    }
  }

  /**
   * Clears all MSAL cache when tokens are invalid/corrupted
   * Call this when receiving 400 Bad Request errors from token endpoint
   */
  clearMsalCache(): void {
    try {
      // Clear active account first
      this.msalService.instance.setActiveAccount(null);

      // Clear interaction state
      this.clearInteractionState();

      // Clear local authentication state
      this.userSubject.next(null);
      this.isAuthenticatedSubject.next(false);

      // Clear MSAL cache keys from localStorage
      // MSAL stores tokens and accounts in localStorage with specific prefixes
      try {
        const msalKeys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.startsWith('msal.') || key.includes('msal') || key.includes('login.microsoftonline.com'))) {
            msalKeys.push(key);
          }
        }
        msalKeys.forEach(key => {
          try {
            localStorage.removeItem(key);
          } catch (e) {
            // Ignore errors
          }
        });

        // Also clear sessionStorage
        const sessionKeys: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && (key.startsWith('msal.') || key.includes('msal') || key.includes('login.microsoftonline.com'))) {
            sessionKeys.push(key);
          }
        }
        sessionKeys.forEach(key => {
          try {
            sessionStorage.removeItem(key);
          } catch (e) {
            // Ignore errors
          }
        });
      } catch (e) {
        // Ignore errors
      }

      // MSAL cache cleared silently
    } catch (error) {
      console.error('Error clearing MSAL cache:', error);
    }
  }

  private async loadUserInfo(): Promise<void> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      return;
    }

    let groups: string[] = [];
    
    // Acquire access token first - we need it for API calls
    let tokenResult: AuthenticationResult | null = null;
    try {
      tokenResult = await this.msalService.instance.acquireTokenSilent({
        scopes: apiScopes.scopes,
        account: account
      }).catch(() => null);
    } catch (e) {
      // Failed to acquire token silently
    }

    // PRIORITY 1: Try to fetch from API endpoint (backend is source of truth for groups and access roles)
    let backendRoles: BackendAccessRoles | null = null;
    if (tokenResult?.accessToken) {
      try {
        const apiResult = await this.fetchUserFromAPI();
        if (apiResult?.groups && apiResult.groups.length > 0) {
          groups = apiResult.groups;
          if (apiResult.roles) {
            backendRoles = apiResult.roles;
          }
        }
      } catch (e) {
        // API call failed, will try fallback methods below
      }
    }

    // PRIORITY 2: Fallback to ID token if API didn't return groups
    if (groups.length === 0) {
      const raw = account.idTokenClaims?.['groups'];
      const idTokenGroups = this.normalizeGroupsClaim(raw);
      if (idTokenGroups.length > 0) groups = idTokenGroups;
    }

    // PRIORITY 3: Fallback to access token if still no groups
    if (groups.length === 0 && tokenResult?.accessToken) {
      try {
        const base64Url = tokenResult.accessToken.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const accessTokenClaims = JSON.parse(jsonPayload);
        const accessTokenGroups = this.normalizeGroupsClaim(accessTokenClaims['groups']);
        if (accessTokenGroups.length > 0) groups = accessTokenGroups;
      } catch (e) {
        // Failed to decode access token
      }
    }

    // PRIORITY 4: Fallback to Graph API if we have overage claims
    if (groups.length === 0) {
      const claimNames = account.idTokenClaims?.['_claim_names'] as any;
      const claimSources = account.idTokenClaims?.['_claim_sources'] as any;
      if (claimNames?.groups || claimSources) {
        try {
          const graphGroups = await this.fetchGroupsFromGraphAPI(account);
          if (graphGroups && graphGroups.length > 0) {
            groups = graphGroups;
          }
        } catch (graphError) {
          // Failed to fetch from Graph API, continue with empty groups
        }
      }
    }

    const userInfo: UserInfo = {
      displayName: account.name || '',
      email: account.username || '',
      groups: groups,
      isAdmin: false,
      isPM: false,
      isFinance: false,
      isPc: false,
      isSoftware: false,
      isAccounting: false,
      isMarketing: false,
      isSales: false,
      isIt: false,
      isLnD: false,
      isOperations: false,
      isSupport: false
    };

    // Use backend-computed roles when available (single source of truth); otherwise derive from group IDs
    if (backendRoles) {
      userInfo.isAdmin = backendRoles.isAdmin;
      userInfo.isPM = backendRoles.isPM;
      userInfo.isFinance = backendRoles.isFinance;
      userInfo.isPc = backendRoles.isPc;
      userInfo.isSoftware = backendRoles.isSoftware;
      userInfo.isAccounting = backendRoles.isAccounting;
      userInfo.isMarketing = backendRoles.isMarketing;
      userInfo.isSales = backendRoles.isSales ?? userInfo.groups.includes(groupIds.salesGroupId);
      userInfo.isIt = backendRoles.isIt ?? userInfo.groups.includes(groupIds.itGroupId);
      userInfo.isLnD = backendRoles.isLnD ?? userInfo.groups.includes(groupIds.lndGroupId);
      userInfo.isOperations = backendRoles.isOperations ?? this.isInGroup(userInfo.groups, groupIds.operationsGroupId);
      userInfo.isSupport = backendRoles.isSupport ?? this.isInGroup(userInfo.groups, groupIds.supportGroupId);
    } else {
      const adminGroupId = this.getAdminGroupId();
      const pmGroupId = this.getPMGroupId();
      const financeGroupId = this.getFinanceGroupId();
      const pcGroupId = this.getPCGroupId();
      const softwareGroupId = this.getSoftwareGroupId();
      const accountingGroupId = this.getAccountingGroupId();
      const marketingGroupId = this.getMarketingGroupId();
      const salesGroupId = this.getSalesGroupId();
      userInfo.isAdmin = userInfo.groups.includes(adminGroupId);
      userInfo.isPM = userInfo.groups.includes(pmGroupId);
      userInfo.isFinance = userInfo.groups.includes(financeGroupId);
      userInfo.isPc = userInfo.groups.includes(pcGroupId);
      userInfo.isSoftware = userInfo.groups.includes(softwareGroupId);
      userInfo.isAccounting = userInfo.groups.includes(accountingGroupId);
      userInfo.isMarketing = userInfo.groups.includes(marketingGroupId);
      userInfo.isSales = userInfo.groups.includes(salesGroupId);
      userInfo.isIt = userInfo.groups.includes(groupIds.itGroupId);
      userInfo.isLnD = userInfo.groups.includes(groupIds.lndGroupId);
      userInfo.isOperations = this.isInGroup(userInfo.groups, groupIds.operationsGroupId);
      userInfo.isSupport = this.isInGroup(userInfo.groups, groupIds.supportGroupId);
    }

    this.userSubject.next(userInfo);
  }

  getUser(): UserInfo | null {
    return this.userSubject.value;
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  hasAdminAccess(): boolean {
    const user = this.userSubject.value;
    return user?.isAdmin || false;
  }

  hasPMAccess(): boolean {
    const user = this.userSubject.value;
    return user?.isAdmin || user?.isPM || false;
  }

  hasFinanceAccess(): boolean {
    const user = this.userSubject.value;
    return user?.isAdmin || user?.isFinance || false;
  }

  hasPCAccess(): boolean {
    const user = this.userSubject.value;
    return user?.isAdmin || user?.isPc || false;
  }

  hasITAccess(): boolean {
    const user = this.userSubject.value;
    return user?.isIt || false;
  }

  hasLnDAccess(): boolean {
    const user = this.userSubject.value;
    return user?.isLnD || false;
  }

  hasOperationsAccess(): boolean {
    const user = this.userSubject.value;
    return user?.isOperations || false;
  }

  hasSupportAccess(): boolean {
    const user = this.userSubject.value;
    return user?.isSupport || false;
  }

  /** Operations and Support are work-log-only groups: Enter Work Log + Draft Work Logs. */
  hasWorkLogOnlyAccess(): boolean {
    const user = this.userSubject.value;
    return !!(user?.isOperations || user?.isSupport);
  }

  hasOnboardingPortalAccess(): boolean {
    const user = this.userSubject.value;
    return !!(user?.isAdmin || user?.isPc || user?.isIt || user?.isLnD);
  }

  private getAdminGroupId(): string {
    return groupIds.adminGroupId;
  }

  private getPMGroupId(): string {
    return groupIds.pmGroupId;
  }

  private getFinanceGroupId(): string {
    return groupIds.financeGroupId;
  }

  private getPCGroupId(): string {
    return groupIds.pcGroupId;
  }

  private getSoftwareGroupId(): string {
    return groupIds.softwareGroupId;
  }

  private getAccountingGroupId(): string {
    return groupIds.accountingGroupId;
  }

  private getMarketingGroupId(): string {
    return groupIds.marketingGroupId;
  }

  private getSalesGroupId(): string {
    return groupIds.salesGroupId;
  }

  private getOperationsGroupId(): string {
    return groupIds.operationsGroupId;
  }

  private getSupportGroupId(): string {
    return groupIds.supportGroupId;
  }

  /**
   * Group membership test that never matches on an unconfigured group id.
   * Group ids are injected at build time and are '' when the App Service setting is
   * missing, so a bare includes() would be decided by whatever happens to be in the
   * claim rather than by configuration.
   */
  private isInGroup(groups: string[], groupId: string): boolean {
    return !!groupId && groups.includes(groupId);
  }

  /** Normalize token "groups" claim (can be string or string[]) to string[]. */
  private normalizeGroupsClaim(raw: unknown): string[] {
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw.filter((g): g is string => typeof g === 'string' && g.length > 0);
    if (typeof raw === 'string' && raw.length > 0) return [raw];
    return [];
  }

  /**
   * Fetches user info from the backend API (groups + backend-computed access roles).
   * Backend is the source of truth for access when this call succeeds.
   */
  private async fetchUserFromAPI(): Promise<{ groups: string[]; roles?: BackendAccessRoles } | null> {
    type UserMeResponse = { groups: string[]; email?: string; name?: string; roles?: BackendAccessRoles };
    try {
      const response = await firstValueFrom(
        this.http.get<UserMeResponse>(`${environment.apiUrl}/User/me`).pipe(
          timeout(5000), // 5 second timeout - fail fast if API is slow
          catchError(() => of<UserMeResponse>({ groups: [] }))
        )
      );
      if (response?.groups && Array.isArray(response.groups)) {
        return { groups: response.groups, roles: response.roles };
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Fetches user groups from Microsoft Graph API
   * This is used as a fallback when groups are not in the token (e.g., group overage)
   */
  private async fetchGroupsFromGraphAPI(account: any): Promise<string[]> {
    try {
      // Try to acquire token for Graph API
      const graphTokenResult = await this.msalService.instance.acquireTokenSilent({
        scopes: ['https://graph.microsoft.com/User.Read'],
        account: account
      }).catch(() => null);

      if (!graphTokenResult?.accessToken) {
        return [];
      }

      // Fetch groups from Microsoft Graph API
      const response = await fetch('https://graph.microsoft.com/v1.0/me/memberOf', {
        headers: {
          'Authorization': `Bearer ${graphTokenResult.accessToken}`
        }
      });

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      const groups: string[] = [];
      
      if (data.value && Array.isArray(data.value)) {
        for (const group of data.value) {
          if (group.id) {
            groups.push(group.id);
          }
        }
      }

      return groups;
    } catch (error) {
      // Failed to fetch from Graph API
      return [];
    }
  }
}

